import { Component, computed, effect, inject, input, output, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { OrganizationModel, UpdateOrganizationRequest } from '../../../../core/models/data/organization.model';
import { OrganizationSettingModel, UpdateOrganizationInternalSettingRequest, UpdateOrganizationSettingRequest } from '../../../../core/models/data/organization-setting.model';
import { ServiceResultModel } from '../../../../core/models/service/service-result.model';
import { OrganizationService } from '../../../../core/services/organization.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { BillingStatuses, PricingTiers } from '../../../../core/enums/data.enums';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { TimezoneDropdown } from '../../../../parts/timezone-dropdown/timezone-dropdown';
import { CurrencyDropdown } from '../../../../parts/currency-dropdown/currency-dropdown';
import { InputLabel } from '../../../../parts/input-label/input-label.component';
import { LocalizationConstants } from '../../../../core/constants/settings.constants';
import { OrganizationsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { CustomValidators } from '../../../../core/validators/custom-validators';
import { FormValidatorMessages } from '../../../../core/enums/forms.enums';
import { FormatLabelPipe } from '../../../../core/pipes/format-label/format-label.pipe';
import { DisableWithoutPermissionDirective } from '../../../../core/directives/disable-without-permission/disable-without-permission.directive';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { OrganizationPermission } from '../../../../core/enums/permissions.enums';

@Component({
  selector: 'ms-organization-general-settings',
  imports: [
    ReactiveFormsModule,
    TimezoneDropdown,
    CurrencyDropdown,
    InputLabel,
    InfoModalComponent,
    FormatLabelPipe,
    DisableWithoutPermissionDirective,
    IfHasPermissionDirective
  ],
  templateUrl: './organization-general-settings.component.html'
})
export class OrganizationGeneralSettingsComponent {

  private fb = inject(FormBuilder);
  private organizationService = inject(OrganizationService);
  private bootstrapService = inject(BootstrapService);
  private userSessionService = inject(UserSessionService);

  public organization = input<OrganizationModel | undefined>(undefined);
  public organizationSettingsUpdated = output<OrganizationSettingModel>();
  public organizationUpdated = output<OrganizationModel>();

  public generalSettingsForm: FormGroup;
  public isSubmitting = signal<boolean>(false);
  public isLoading = signal<boolean>(true);
  public organizationSettings: OrganizationSettingModel | undefined;

  // Expose enums for template
  public BillingStatuses = BillingStatuses;
  public PricingTiers = PricingTiers;
  public FormValidatorMessages = FormValidatorMessages;
  public OrganizationsMessagingConstants = OrganizationsMessagingConstants;
  public OrganizationPermission = OrganizationPermission;

  private initialFormValues = signal<{
    name: string | undefined;
    billingStatus: string | undefined;
    pricingTier: string | undefined;
    slug: string | undefined;
    replyToEmailAddress: string | undefined;
    timezone: string | undefined;
    currency: string | undefined;
    includeMembersInDirectoryByDefault: boolean | undefined;
  } | undefined>(undefined);
  private formValues;

  public hasFormBeenModified = computed(() => {
    const initial = this.initialFormValues();
    const current = this.formValues();
    if (!initial || !current) return false;
    return initial.name !== current.name ||
           initial.billingStatus !== current.billingStatus ||
           initial.pricingTier !== current.pricingTier ||
           initial.slug !== current.slug ||
           initial.replyToEmailAddress !== current.replyToEmailAddress ||
           initial.timezone !== current.timezone ||
           initial.currency !== current.currency ||
           initial.includeMembersInDirectoryByDefault !== current.includeMembersInDirectoryByDefault;
  });

  @ViewChild('generalSettingsInfoModalRef') infoModalRef!: InfoModalComponent;

  constructor() {
    this.generalSettingsForm = this.fb.group({
      name: ['', [Validators.required]],
      billingStatus: [undefined],
      pricingTier: [undefined],
      slug: ['', [Validators.required, CustomValidators.slug]],
      replyToEmailAddress: ['', [Validators.required, CustomValidators.email]],
      timezone: [undefined],
      currency: [undefined],
      includeMembersInDirectoryByDefault: [true]
    });

    this.formValues = toSignal(this.generalSettingsForm.valueChanges, { initialValue: this.generalSettingsForm.value });

    effect(() => {
      const org = this.organization();
      if (org?.organizationId) {
        this.loadOrganizationSettings(org.organizationId);
      }
    });
  }

  private async loadOrganizationSettings(organizationId: number): Promise<void> {
    this.isLoading.set(true);
    const orgName = this.organization()?.name ?? '';
    const result = await this.organizationService.getOrganizationSettings(organizationId);
    if (result.success && result.data) {
      this.organizationSettings = result.data;
      this.generalSettingsForm.patchValue({
        name: orgName,
        billingStatus: result.data.billingStatus,
        pricingTier: result.data.pricingTier,
        slug: result.data.slug || '',
        replyToEmailAddress: result.data.replyToEmailAddress || '',
        timezone: result.data.timeZoneId || LocalizationConstants.DEFAULT_TIMEZONE_ID(),
        currency: result.data.currency?.currencyId || LocalizationConstants.DEFAULT_CURRENCY_ID(),
        includeMembersInDirectoryByDefault: result.data.includeMembersInDirectoryByDefault
      });
    } else {
      this.generalSettingsForm.patchValue({
        name: orgName,
        billingStatus: BillingStatuses.Trial,
        pricingTier: PricingTiers.Standard,
        slug: '',
        replyToEmailAddress: '',
        timezone: LocalizationConstants.DEFAULT_TIMEZONE_ID(),
        currency: LocalizationConstants.DEFAULT_CURRENCY_ID(),
        includeMembersInDirectoryByDefault: true
      });
    }
    this.markEmptyReplyToEmailAddressAsTouched();
    this.snapshotInitialValues();
    this.isLoading.set(false);
  }

  // Organizations saved before the reply-to address became mandatory load with an empty value, so surface the
  // required message straight away rather than leaving Save disabled with no explanation.
  private markEmptyReplyToEmailAddressAsTouched(): void {
    const replyToEmailAddressControl = this.generalSettingsForm.get('replyToEmailAddress');
    if (!replyToEmailAddressControl?.value) {
      replyToEmailAddressControl?.markAsTouched();
    }
  }

  private snapshotInitialValues(): void {
    this.initialFormValues.set({
      name: this.generalSettingsForm.get('name')?.value,
      billingStatus: this.generalSettingsForm.get('billingStatus')?.value,
      pricingTier: this.generalSettingsForm.get('pricingTier')?.value,
      slug: this.generalSettingsForm.get('slug')?.value,
      replyToEmailAddress: this.generalSettingsForm.get('replyToEmailAddress')?.value,
      timezone: this.generalSettingsForm.get('timezone')?.value,
      currency: this.generalSettingsForm.get('currency')?.value,
      includeMembersInDirectoryByDefault: this.generalSettingsForm.get('includeMembersInDirectoryByDefault')?.value
    });
  }

  public async onSave(): Promise<void> {
    if (this.generalSettingsForm.invalid) {
      this.generalSettingsForm.markAllAsTouched();
      return;
    }

    const organization = this.organization();
    const organizationId = organization?.organizationId;
    if (!organizationId || !this.organizationSettings) return;

    const initial = this.initialFormValues();
    const nameValue: string = this.generalSettingsForm.get('name')?.value ?? '';
    const nameChanged = !!initial && initial.name !== nameValue;
    const tenantSettingsChanged = !!initial && (
      initial.replyToEmailAddress !== this.generalSettingsForm.get('replyToEmailAddress')?.value ||
      initial.timezone !== this.generalSettingsForm.get('timezone')?.value ||
      initial.currency !== this.generalSettingsForm.get('currency')?.value ||
      initial.includeMembersInDirectoryByDefault !== this.generalSettingsForm.get('includeMembersInDirectoryByDefault')?.value
    );
    const internalSettingsChanged = !!initial && (
      initial.billingStatus !== this.generalSettingsForm.get('billingStatus')?.value ||
      initial.pricingTier !== this.generalSettingsForm.get('pricingTier')?.value ||
      initial.slug !== this.generalSettingsForm.get('slug')?.value
    );

    if (!nameChanged && !tenantSettingsChanged && !internalSettingsChanged) return;

    this.isSubmitting.set(true);

    try {
      const namePromise: Promise<ServiceResultModel<OrganizationModel> | null> = nameChanged
        ? this.organizationService.updateOrganization(organizationId, <UpdateOrganizationRequest>{
            name: nameValue,
            clearAvatarFileId: false
          })
        : Promise.resolve(null);

      let tenantRequest: UpdateOrganizationSettingRequest | null = null;
      if (tenantSettingsChanged) {
        const timezoneValue = this.generalSettingsForm.get('timezone')?.value;
        const timeZoneId = typeof timezoneValue === 'string'
          ? timezoneValue
          : timezoneValue?.id ?? this.organizationSettings?.timeZoneId;

        const currencyValue = this.generalSettingsForm.get('currency')?.value;
        const currencyId = typeof currencyValue === 'number'
          ? currencyValue
          : currencyValue?.currencyId ?? this.organizationSettings?.currency?.currencyId;

        if (!currencyId) {
          this.infoModalRef.modalType.set(ModalTypes.Failure);
          this.infoModalRef.message.set('Please select a valid currency.');
          this.bootstrapService.manageModal("#generalSettingsInfoModal", ModalActions.Show);
          return;
        }

        // Every field goes on every save: this PATCH replaces the settings record rather than merging
        // into it, so omitting one resets it. That is why isStripeOnboardingComplete is carried
        // through untouched, and why the directory default has to be too.
        tenantRequest = {
          replyToEmailAddress: this.generalSettingsForm.get('replyToEmailAddress')?.value ?? this.organizationSettings?.replyToEmailAddress ?? '',
          isStripeOnboardingComplete: this.organizationSettings?.isStripeOnboardingComplete ?? false,
          timeZoneId: timeZoneId,
          currency: currencyId,
          includeMembersInDirectoryByDefault: this.generalSettingsForm.get('includeMembersInDirectoryByDefault')?.value
            ?? this.organizationSettings?.includeMembersInDirectoryByDefault
            ?? true
        };
      }

      let internalRequest: UpdateOrganizationInternalSettingRequest | null = null;
      if (internalSettingsChanged) {
        internalRequest = {
          billingStatus: this.generalSettingsForm.get('billingStatus')?.value ?? this.organizationSettings?.billingStatus,
          pricingTier: this.generalSettingsForm.get('pricingTier')?.value ?? this.organizationSettings?.pricingTier,
          slug: this.generalSettingsForm.get('slug')?.value ?? this.organizationSettings?.slug ?? ''
        };
      }

      const tenantPromise: Promise<ServiceResultModel<OrganizationSettingModel> | null> = tenantRequest
        ? this.organizationService.updateOrganizationSettings(organizationId, tenantRequest)
        : Promise.resolve(null);
      const internalPromise: Promise<ServiceResultModel<OrganizationSettingModel> | null> = internalRequest
        ? this.organizationService.updateOrganizationInternalSettings(organizationId, internalRequest)
        : Promise.resolve(null);

      const [nameResponse, tenantResponse, internalResponse] = await Promise.all([namePromise, tenantPromise, internalPromise]);

      const nameSucceeded = !nameResponse || nameResponse.success === true;
      const tenantSucceeded = !tenantResponse || tenantResponse.success === true;
      const internalSucceeded = !internalResponse || internalResponse.success === true;
      const allSucceeded = nameSucceeded && tenantSucceeded && internalSucceeded;

      if (nameSucceeded && nameResponse?.data) {
        this.organizationUpdated.emit(nameResponse.data);
      }

      // internal-settings response is emitted last so it wins when both endpoints ran, matching the newer state.
      const latestSettings = internalResponse?.data ?? tenantResponse?.data;
      if ((tenantSucceeded || internalSucceeded) && latestSettings) {
        this.organizationSettings = latestSettings;
        await this.userSessionService.refreshOrganizationSettings();
        this.organizationSettingsUpdated.emit(latestSettings);
      }

      if (allSucceeded) {
        this.snapshotInitialValues();
        this.infoModalRef.modalType.set(ModalTypes.Success);
        this.infoModalRef.message.set(internalResponse?.message ?? tenantResponse?.message ?? nameResponse?.message ?? 'Saved successfully.');
      } else {
        this.infoModalRef.modalType.set(ModalTypes.Failure);
        const failure = !nameSucceeded ? nameResponse : !tenantSucceeded ? tenantResponse : internalResponse;
        this.infoModalRef.message.set(failure?.message ?? 'Failed to save changes.');
      }

      this.bootstrapService.manageModal("#generalSettingsInfoModal", ModalActions.Show);
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
